import React, { useState, useEffect, lazy, Suspense, useMemo } from 'react';
import { BrowserRouter as Router, Switch, Route, useHistory, useLocation } from 'react-router-dom';
import { analytics } from './utils/analytics.ts';

import ErrorBoundary from './components/ErrorBoundary.tsx';
import FloatingActionButton from './components/FloatingActionButton.tsx';
import Header from './components/Header.tsx';
import LoginModal from './components/LoginModal.tsx';
import ClubLeaderRoute from './components/ClubLeaderRoute.tsx';
import LoadingSpinner from './components/LoadingSpinner.tsx';
import ProtectedRoute from './components/ProtectedRoute.tsx';
import AuthRequiredRoute from './components/AuthRequiredRoute.tsx';
import { AuthProvider, useAuth } from './contexts/AuthContext.tsx';
import { ToastProvider } from './contexts/ToastContext.tsx';
import { useIsMobile } from './hooks/useMediaQuery.ts';
import { supabase } from './lib/supabase.ts';
import { initSentry } from './utils/sentry.ts';
import { handleLoginRequired } from './utils/googleAuth.ts';

const Clubs = lazy(() => import('./pages/Clubs.tsx'));
const Profile = lazy(() => import('./pages/Profile/index.tsx'));
const CreateClub = lazy(() => import('./pages/CreateClub/index.tsx'));
const ClubDetail = lazy(() => import('./pages/ClubDetail/index.tsx'));
const ClubBoard = lazy(() => import('./pages/ClubBoard/index.tsx'));
const Onboarding = lazy(() => import('./pages/Onboarding.tsx'));
const WriteReview = lazy(() => import('./pages/WriteReview.tsx'));
const PreparationWriting = lazy(() => import('./pages/PreparationWriting.tsx'));
const WritingHub = lazy(() => import('./pages/WritingHub.tsx'));
const FeedWrite = lazy(() => import('./pages/FeedWrite.tsx'));
const QnA = lazy(() => import('./pages/QnA.tsx'));
const Welcome = lazy(() => import('./pages/Welcome.tsx'));
const NoticeBoard = lazy(() => import('./pages/NoticeBoard.tsx'));
const NoticeWrite = lazy(() => import('./pages/NoticeWrite.tsx'));
const NoticeDetail = lazy(() => import('./pages/NoticeDetail.tsx'));
const NoticeEdit = lazy(() => import('./pages/NoticeEdit.tsx'));
const MarketplaceMain = lazy(() => import('./pages/Marketplace/MarketplaceMain.tsx'));
const BookRegister = lazy(() => import('./pages/Marketplace/BookRegister.tsx'));
const BookDetail = lazy(() => import('./pages/Marketplace/BookDetail.tsx'));
const BookEdit = lazy(() => import('./pages/Marketplace/BookEdit.tsx'));
const MyBooks = lazy(() => import('./pages/Marketplace/MyBooks.tsx'));
const TransactionDetail = lazy(() => import('./pages/Marketplace/TransactionDetail.tsx'));
const BookDataFiller = lazy(() => import('./pages/Marketplace/BookDataFiller.tsx'));
const WriteBookReview = lazy(() => import('./pages/Marketplace/WriteBookReview.tsx'));

const Privacy = lazy(() => import('./pages/Privacy.tsx'));
const Terms = lazy(() => import('./pages/Terms.tsx'));
const EventsList = lazy(() => import('./pages/Events/EventsList.tsx'));
const EventDetail = lazy(() => import('./pages/Events/EventDetail.tsx'));
const EventCreate = lazy(() => import('./pages/Events/EventCreate.tsx'));

const pages = [
  { id: 'marketplace', title: 'MARKET' },
  { id: 'mybooks', title: 'MY BOOKS' },
  { id: 'clubs', component: Clubs, title: 'CLUBS' },
  { id: 'qna', title: 'Q&A' },
];

interface MainAppProps {
  initialPage?: number;
}

const MainApp: React.FC<MainAppProps> = ({ initialPage = 0 }) => {
  const history = useHistory();
  const location = useLocation();
  const pageContainerRef = React.useRef<HTMLDivElement>(null);
  const [currentPage, setCurrentPage] = useState(initialPage);
  const { user, signInWithGoogle, signOut } = useAuth();
  const isMobile = useIsMobile();

  // 마켓플레이스 알림
  useEffect(() => {
    if (!user) return;

    import('./hooks/useMarketplaceNotifications.ts').then(({ useMarketplaceNotifications }) => {
      // 훅을 직접 호출할 수 없으므로 수동으로 구독 설정
    }).catch(() => {});
  }, [user]);

  const hideFloatingButtonPaths = ['/write', '/create-club', '/edit-club', '/events', '/qna'];
  const shouldShowFloatingButton = !hideFloatingButtonPaths.some(path =>
    location.pathname.includes(path)
  );



  const [showLoginModal, setShowLoginModal] = useState(false);

  const handleLogin = async () => {
    try {
      if (user) {
        const confirmSwitch = window.confirm(
          '다른 계정으로 로그인하시겠습니까? 현재 계정에서 로그아웃됩니다.'
        );
        if (confirmSwitch) {
          await signOut();
          setTimeout(async () => {
            await signInWithGoogle();
          }, 500);
        }
        return;
      }
      await signInWithGoogle();
    } catch (error) {
      console.error('Login error:', error);
      alert('로그인 중 오류가 발생했습니다.');
    }
  };

  const pageUrls = useMemo(() => ['/marketplace', '/clubs', '/events', '/qna'], []);

  useEffect(() => {
    const currentPath = location.pathname;
    let pageIndex = pageUrls.indexOf(currentPath);
    if (currentPath === '/' && pageIndex === -1) {
      pageIndex = 0;
    }
    if (pageIndex !== -1) {
      setCurrentPage(pageIndex);
      if (pageContainerRef.current) {
        pageContainerRef.current.scrollTop = 0;
      }
    }
    analytics.trackPageView(currentPath);
  }, [location.pathname, pageUrls]);

  return (
    <div
      style={{
        minHeight: '100vh',
        position: 'relative',
        background: '#fafafa',
      }}
    >
      <Header
        currentPage={currentPage}
        totalPages={pages.length}
        pageTitle={pages[currentPage]?.title || ''}
        onPageChange={page => {
          setCurrentPage(page);
          history.push(pageUrls[page]);
        }}
        onProfileClick={() => {
          if (user) {
            history.push('/profile');
          } else {
            handleLoginRequired(() => setShowLoginModal(true), signInWithGoogle);
          }
        }}
      />

      <div
        ref={pageContainerRef}
        data-page-container
        style={{
          background: '#f9f7f4',
          paddingTop: isMobile ? '88px' : '100px',
          paddingBottom: isMobile ? '90px' : '0',
          minHeight: '100vh',
          boxSizing: 'border-box',
        }}
      >
        {currentPage === 0 && <MarketplaceMain />}
        {currentPage === 1 && <Clubs isLoggedIn={!!user} onLogin={handleLogin} />}
        {currentPage === 2 && <EventsList />}
        {currentPage === 3 && <QnA />}
      </div>

      {shouldShowFloatingButton && !isMobile && (
        <FloatingActionButton
          style={{
            position: 'fixed',
            bottom: '30px',
            right: '30px',
            zIndex: 1000,
          }}
        />
      )}

      <LoginModal isOpen={showLoginModal} onClose={() => setShowLoginModal(false)} />

      {!isMobile && (
        <footer
          style={{
            textAlign: 'center',
            padding: '1.5rem',
            borderTop: '1px solid #e8e6e1',
            fontSize: '0.8rem',
            color: '#9b9b9b',
            background: '#f9f7f4',
          }}
        >
          <a href="/privacy" style={{ color: '#8b7355', textDecoration: 'underline', marginRight: '1.5rem' }}>
            개인정보처리방침
          </a>
          <a href="/terms" style={{ color: '#8b7355', textDecoration: 'underline' }}>
            이용약관
          </a>
          <span style={{ marginLeft: '1.5rem' }}>© 2025 Tokyo Book Tree</span>
        </footer>
      )}
    </div>
  );
};

const App: React.FC = () => {
  useEffect(() => {
    initSentry();
  }, []);

  useEffect(() => {
    let hiddenAt: number | null = null;
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        hiddenAt = Date.now();
      } else if (document.visibilityState === 'visible' && hiddenAt) {
        const elapsed = Date.now() - hiddenAt;
        if (elapsed > 10 * 60 * 1000) {
          window.location.reload();
        }
        hiddenAt = null;
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, []);

  return (
    <ErrorBoundary>
      <AuthProvider>
        <ToastProvider>
          <Router>
            <Suspense fallback={<LoadingSpinner fullScreen />}>
              <Switch>
                <Route path="/welcome" component={Welcome} />
                <Route path="/events/create" component={EventCreate} />
                <Route path="/events/:slug/edit" component={EventCreate} />
                <Route path="/events/:slug" component={EventDetail} />
                <Route path="/events" render={() => <MainApp initialPage={2} />} />
                <Route path="/onboarding" component={Onboarding} />
                <Route path="/privacy" component={Privacy} />
                <Route path="/terms" component={Terms} />
                <Route
                  path="/marketplace/admin/fill-data"
                  render={() => (
                    <ProtectedRoute>
                      <BookDataFiller />
                    </ProtectedRoute>
                  )}
                />
                <Route
                  path="/marketplace/my-books"
                  render={() => (
                    <AuthRequiredRoute>
                      <MyBooks />
                    </AuthRequiredRoute>
                  )}
                />
                <Route
                  path="/marketplace/register"
                  render={() => (
                    <ProtectedRoute>
                      <BookRegister />
                    </ProtectedRoute>
                  )}
                />
                <Route
                  path="/marketplace/transaction/:transactionId/review"
                  render={() => (
                    <AuthRequiredRoute>
                      <WriteBookReview />
                    </AuthRequiredRoute>
                  )}
                />
                <Route
                  path="/marketplace/transaction/:transactionId"
                  render={() => (
                    <AuthRequiredRoute>
                      <TransactionDetail />
                    </AuthRequiredRoute>
                  )}
                />
                <Route
                  path="/marketplace/book/:bookId/edit"
                  render={() => (
                    <ProtectedRoute>
                      <BookEdit />
                    </ProtectedRoute>
                  )}
                />
                <Route
                  path="/marketplace/book/:bookId"
                  render={() => (
                    <ProtectedRoute>
                      <BookDetail />
                    </ProtectedRoute>
                  )}
                />
                <Route
                  path="/marketplace"
                  render={() => (
                    <ProtectedRoute>
                      <MainApp initialPage={0} />
                    </ProtectedRoute>
                  )}
                />
                <Route
                  path="/club/:slug"
                  render={() => (
                    <ProtectedRoute>
                      <ClubDetail />
                    </ProtectedRoute>
                  )}
                />
                <Route
                  path="/clubboard/:slug"
                  render={() => (
                    <ProtectedRoute>
                      <ClubLeaderRoute>
                        <ClubBoard />
                      </ClubLeaderRoute>
                    </ProtectedRoute>
                  )}
                />
                <Route
                  path="/profile"
                  render={() => (
                    <ProtectedRoute>
                      <Profile />
                    </ProtectedRoute>
                  )}
                />
                <Route
                  path="/create-club"
                  render={() => (
                    <ProtectedRoute>
                      <CreateClub />
                    </ProtectedRoute>
                  )}
                />
                <Route
                  path="/edit-club/:slug"
                  render={() => (
                    <ProtectedRoute>
                      <ClubLeaderRoute>
                        <CreateClub />
                      </ClubLeaderRoute>
                    </ProtectedRoute>
                  )}
                />
                <Route
                  path="/write/preparation/:scheduleId"
                  render={() => (
                    <ProtectedRoute>
                      <PreparationWriting />
                    </ProtectedRoute>
                  )}
                />
                <Route
                  path="/write/review/:clubId"
                  render={() => (
                    <ProtectedRoute>
                      <WriteReview />
                    </ProtectedRoute>
                  )}
                />
                <Route
                  path="/write/feed"
                  render={() => (
                    <ProtectedRoute>
                      <FeedWrite />
                    </ProtectedRoute>
                  )}
                />
                <Route
                  path="/writing-hub"
                  render={() => (
                    <ProtectedRoute>
                      <WritingHub />
                    </ProtectedRoute>
                  )}
                />
                <Route
                  path="/qna"
                  render={() => (
                    <MainApp initialPage={3} />
                  )}
                />
                <Route
                  path="/notices/write"
                  render={() => (
                    <ProtectedRoute>
                      <NoticeWrite />
                    </ProtectedRoute>
                  )}
                />
                <Route
                  path="/notices/:id/edit"
                  render={() => (
                    <ProtectedRoute>
                      <NoticeEdit />
                    </ProtectedRoute>
                  )}
                />
                <Route
                  path="/notices/:id"
                  render={() => (
                    <ProtectedRoute>
                      <NoticeDetail />
                    </ProtectedRoute>
                  )}
                />
                <Route
                  path="/notices"
                  render={() => (
                    <ProtectedRoute>
                      <NoticeBoard />
                    </ProtectedRoute>
                  )}
                />
                <Route
                  path="/clubs"
                  render={() => (
                    <ProtectedRoute>
                      <MainApp initialPage={1} />
                    </ProtectedRoute>
                  )}
                />
                <Route
                  path="/"
                  render={() => (
                    <ProtectedRoute>
                      <MainApp />
                    </ProtectedRoute>
                  )}
                />
              </Switch>
            </Suspense>
          </Router>
        </ToastProvider>
      </AuthProvider>
    </ErrorBoundary>
  );
};

export default App;
